import React, { ReactNode, useEffect } from "react";
import { useForm, FieldValues, DefaultValues, FormProvider  } from "react-hook-form";
import { FieldSectionConfig } from "../../../../../models/Models";
import FormFieldsRenderer from "./FormFieldsRenderer";
import Modal from "../../../../../context/modal/Modal";
import useModal from "../../../../../hooks/useModal";

interface EditFormProps<T extends FieldValues> {
    modalId: string;
    config?: FieldSectionConfig<T>[]
    initialValues: T
    onSubmit: (data: T) => void
    children?: ReactNode
}

const EditForm = <T extends FieldValues>  ({
    config,
    initialValues,
    onSubmit,
    modalId,
    children,
}: EditFormProps<T>) => {
    const { dialogRef } = useModal();
    const methods = useForm<T>({ defaultValues: initialValues as DefaultValues<T> });
    const {
        handleSubmit,
        reset,
    } = methods;

    useEffect(() => {
        reset(initialValues);

        const dialog = dialogRef.current;
        if (!dialog) return;

        const handleClose = () => reset(initialValues);
        dialog.addEventListener("close", handleClose);
        return () => dialog.removeEventListener("close", handleClose);
    }, [ initialValues, reset, dialogRef ]);

    const submit = (data: T) => {
        const dialog = dialogRef.current;
        if (dialog) dialog.close();

        const stringifiedData = JSON.stringify(data);
        const stringifiedInitialValues = JSON.stringify(initialValues);

        reset(initialValues);
        if (stringifiedData === stringifiedInitialValues)
            return;

        return onSubmit(data);
    };

    return (
        <>
            <Modal.Body>
                <FormProvider {...methods}>
                    <form
                        id={`${modalId}-form`}
                        method={"dialog"}
                        onSubmit={handleSubmit(submit)}
                        className="flex h-full flex-col"
                    >
                        <div className={`flex flex-col gap-3`}>
                            {
                                children && !config && children
                            }
                            {
                                config && !children && config.map((section, i) => (
                                    <div key={`${section.heading}-${i}`} className={`flex flex-col gap-1`}>
                                        {section.heading && <h2 className={`text-md font-bold`}>{section.heading}</h2>}
                                        <div className={`${section.heading ? "p-2" : ""}`}>
                                            {
                                                <FormFieldsRenderer
                                                    config={section.fields}
                                                />
                                            }
                                        </div>
                                    </div>

                                ))
                            }
                        </div>
                    </form>
                </FormProvider>
            </Modal.Body>
            <Modal.Footer>
                <button form={`${modalId}-form`} className={`text-white bg-brand-500 p-2 rounded-md font-bold`}>Submit</button>
            </Modal.Footer>
        </>
    );
};

export default EditForm;
